/**
 * EG Search (Meilisearch) – results renderer.
 *
 * Rendering helpers for the /search page (stats, cards, facets, pagination).
 */
window.EGSearchResultsRender = (function () {
  'use strict';

  const config = window.EGSearchConfig;
  const supportsResponsivePreload = 'imageSrcset' in document.createElement('link');
  // Match the two-column phone grid; larger breakpoints are conservative so
  // thumbnails remain sharp when the sidebar and card gutters change.
  const RESULT_IMAGE_SIZES = '(max-width: 580px) calc(50vw - 46px), (max-width: 989px) 50vw, (max-width: 1200px) 33vw, 25vw';
  let firstImagePreload = null;

  function preloadFirstImage(hits) {
    const source = dedupeHitsByHandle(hits)[0]?.image_url;
    if (!source) {
      firstImagePreload?.remove();
      firstImagePreload = null;
      return;
    }
    const href = resultImageUrl(source);
    if (firstImagePreload?.href === href) return;
    if (!firstImagePreload) {
      firstImagePreload = document.createElement('link');
      firstImagePreload.rel = 'preload';
      firstImagePreload.as = 'image';
      firstImagePreload.fetchPriority = 'high';
      firstImagePreload.imageSrcset = resultImageSrcset(source);
      firstImagePreload.imageSizes = RESULT_IMAGE_SIZES;
      firstImagePreload.href = href;
      document.head.appendChild(firstImagePreload);
    } else {
      firstImagePreload.imageSrcset = resultImageSrcset(source);
      firstImagePreload.href = href;
    }
  }

  // Only resize supported Shopify images; leave other image hosts unchanged.
  function resultImageUrl(source, width = 600) {
    if (!source) return '/no-image.png';
    try {
      const url = new URL(source, window.location.origin);
      if (url.hostname === 'cdn.shopify.com' ||
          (url.origin === window.location.origin && url.pathname.startsWith('/cdn/shop/'))) {
        url.searchParams.set('width', String(width));
        return url.href;
      }
    } catch (_) {}
    return source;
  }

  function resultImageSrcset(source) {
    // Older browsers keep the same 600px URL for both preload and image.
    if (!supportsResponsivePreload) return '';
    if (!source || resultImageUrl(source, 320) === resultImageUrl(source, 600)) return '';
    return [320, 400, 480, 600].map(width => `${resultImageUrl(source, width)} ${width}w`).join(', ');
  }

  // De-dupe hits (the index can return multiple docs per product).
  function dedupeHitsByHandle(hits) {
    if (!hits || !hits.length) return [];

    const unique = [];
    const seen = new Map();

    hits.forEach((hit) => {
      const key = hit && (hit.handle || hit.id || hit.objectID);
      if (!key) {
        unique.push(hit);
        return;
      }

      if (!seen.has(key)) {
        seen.set(key, unique.length);
        unique.push(hit);
        return;
      }

      const idx = seen.get(key);
      const existing = unique[idx];
      const existingPrice = Number(existing?.price);
      const nextPrice = Number(hit?.price);
      if (Number.isFinite(nextPrice) && (!Number.isFinite(existingPrice) || nextPrice < existingPrice)) {
        unique[idx] = hit;
      }
    });

    return unique;
  }

  function renderSortDropdown(currentSort) {
    const opts = [
      { value: '', label: 'Relevance' },
      { value: 'price:asc', label: 'Price: Low to High' },
      { value: 'price:desc', label: 'Price: High to Low' },
      { value: 'title:asc', label: 'Name: A to Z' },
      { value: 'title:desc', label: 'Name: Z to A' },
      { value: 'created_at:desc', label: 'Newest' }
    ];
    return `<div class="meili-search__sort">
      <label>Sort by</label>
      <select onchange="customSearchSetSort(this.value)">
        ${opts.map(o => `<option value="${o.value}" ${currentSort === o.value ? 'selected' : ''}>${o.label}</option>`).join('')}
      </select>
    </div>`;
  }

  function renderStats(data, container, displayedCount, currentSort, isMobileInfiniteScroll) {
    if (!container) return;
    const total = Number(data.nbHits) || 0;
    const perPage = Number(data.hitsPerPage) || 24;
    const page = Number(data.page) || 0;
    const count = Number.isFinite(displayedCount) ? displayedCount : (data.hits || []).length;

    // If we de-duped variant-level docs down to unique products, show totals based on what we
    // actually render (prevents "X results" looking wrong on small result sets).
    const effectiveTotal = (total > 0 && page === 0 && total <= perPage && count < total) ? count : total;

    const start = isMobileInfiniteScroll ? (effectiveTotal > 0 ? 1 : 0) : (effectiveTotal > 0 ? (page * perPage + 1) : 0);
    const end = effectiveTotal > 0
      ? (isMobileInfiniteScroll ? Math.min(count, effectiveTotal) : Math.min(start + Math.max(0, count - 1), effectiveTotal))
      : 0;

    container.innerHTML = `
      <span class="ais-Stats-text">
        ${start} - ${end} out of <strong>${effectiveTotal}</strong> products
      </span>
      ${renderSortDropdown(currentSort)}
    `;
  }

  // Caret icon used on facet headers
  const CARET_SVG = `<svg class="ais-facet--caret" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  // PDP-style quantity dropdowns we want to expose on search result cards.
  const PACK_QTY_HANDLES = new Set([
    'epic-tray-6-cell-germination-dome-and-bottom-tray',
    'epic-tray-4-cell-germination-dome-and-bottom-tray',
    'epic-16-cell-seed-starting-trays',
    'epic-16-cell-seed-starting-trays-pack',
    '1020-universal-bottom-tray',
    'epic-tray-6-cell-garden-propagation-trays',
    'epic-tray-4-cell-garden-propagation-trays',
    '5-cube-pot',
    '3-cube-pot',
  ]);

  const MAPPED_INVENTORY_HANDLE_FALLBACK = new Set([
    'epic-tray-6-cell-germination-dome-and-bottom-tray',
    'epic-tray-4-cell-germination-dome-and-bottom-tray',
    'ultimate-beginner-seed-starting-bundle',
    '3-cube-pot',
    'epic-16-cell-seed-starting-trays',
    '29-tall-birdies-large-4pack',
    '15-short-birdies-large-4pack',
    '29-tall-birdies-medium-4pack',
    '15-short-birdies-medium-4pack',
    '29-tall-birdies-small-4pack',
    '15-short-birdies-small-4pack',
  ]);

  function isMappedComponentInventoryHandle(handle) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return false;

    if (typeof window.EGInventoryOverrides?.shouldApplyVirtualBundleOverwrite === 'function') {
      return window.EGInventoryOverrides.shouldApplyVirtualBundleOverwrite(normalizedHandle);
    }

    if (MAPPED_INVENTORY_HANDLE_FALLBACK.has(normalizedHandle)) return true;

    const getFixedMappedTargetProductId = window.EGInventoryOverrides?.getFixedMappedTargetProductId;
    if (typeof getFixedMappedTargetProductId === 'function') {
      const mappedId = Number(getFixedMappedTargetProductId(normalizedHandle));
      return Number.isFinite(mappedId) && mappedId > 0;
    }

    return false;
  }

  function packQtyDiscountForCount(count) {
    const i = Number(count) || 0;
    if (i <= 1) return 0;
    if (i >= 2 && i <= 4) return 0.10;
    if (i >= 5 && i <= 7) return 0.20;
    if (i >= 8 && i <= 11) return 0.30;
    if (i >= 12 && i <= 15) return 0.35;
    if (i >= 16 && i <= 19) return 0.38;
    return 0.40;
  }

  function formatUSD(amount) {
    const n = Number(amount);
    if (!Number.isFinite(n)) return '$0.00';
    return `$${n.toFixed(2)}`;
  }

  function priceDataAttrs(productId, variantId) {
    const productAttr = productId ? ` data-product-id="${config.escapeHtml(String(productId))}"` : '';
    const variantAttr = variantId ? ` data-variant-id="${config.escapeHtml(String(variantId))}"` : '';
    return `${productAttr}${variantAttr}`;
  }

  function refreshIntelligemsPrices() {
    if (typeof window.igData?.resetDom === 'function') window.igData.resetDom();
  }

  function normalizeMoneyValue(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    // Defensive: if a cents-like number sneaks in, normalize to dollars.
    return n > 999 ? n / 100 : n;
  }

  function resolveDisplayPrices(hit) {
    const priceCandidates = [
      hit?.price,
      hit?.price_min,
      hit?.min_price,
      hit?._formatted?.price
    ];
    const compareCandidates = [
      hit?.compare_at_price,
      hit?.compare_at_price_min,
      hit?.compare_at_price_max,
      hit?.max_compare_at_price,
      hit?.compare_price,
      hit?._formatted?.compare_at_price
    ];

    let price = null;
    for (const candidate of priceCandidates) {
      const normalized = normalizeMoneyValue(candidate);
      if (normalized !== null) {
        price = normalized;
        break;
      }
    }

    let compareAt = null;
    for (const candidate of compareCandidates) {
      const normalized = normalizeMoneyValue(candidate);
      if (normalized !== null && normalized > 0) {
        compareAt = normalized;
        break;
      }
    }

    const hasDiscount = price !== null && compareAt !== null && compareAt > price;
    const discountPercent = hasDiscount ? Math.round(((compareAt - price) * 100) / compareAt) : 0;

    return {
      price: price ?? 0,
      compareAt: compareAt ?? 0,
      hasDiscount,
      discountPercent
    };
  }

  function isMemberPricingEligible(hit) {
    const tags = Array.isArray(hit?.tags) ? hit.tags : [];
    const tagsLower = tags.map((tag) => String(tag || '').toLowerCase().trim());
    if (tagsLower.includes('exclude-member-pricing')) return false;

    if (hit?.gift_card === true || hit?.is_gift_card === true) return false;
    const giftCardText = [
      hit?.product_type,
      hit?.type,
      hit?.handle,
      hit?.title,
    ].map((value) => String(value || '').toLowerCase().trim()).join(' ');
    if (
      giftCardText.includes('gift card') ||
      giftCardText.includes('gift-card') ||
      giftCardText.includes('giftcard')
    ) return false;

    return true;
  }

  function hitHasOrganicBadge(hit) {
    const organic = hit?.organic;
    if (organic === true || organic === 'true' || organic === 1 || organic === '1') return true;
    if (Array.isArray(organic)) {
      return organic.some((value) => {
        const normalized = String(value || '').toLowerCase().trim();
        return normalized === 'true' || normalized === '1' || normalized === 'yes';
      });
    }

    const tags = Array.isArray(hit?.tags) ? hit.tags : [];
    const tagsLower = tags.map((tag) => String(tag || '').toLowerCase().trim());
    return tagsLower.includes('organic') || tagsLower.includes('badge-organic');
  }

  function memberAdjustedPrice(price) {
    const n = Number(price);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.ceil(n * 0.9 * 100) / 100;
  }

  function renderSearchPriceLine(pricing, showMemberPrice, productId, variantId) {
    const basePrice = Number(pricing?.price) || 0;
    const compareAt = Number(pricing?.compareAt) || 0;
    const hasDiscount = pricing?.hasDiscount === true;
    const attrs = priceDataAttrs(productId, variantId);

    if (showMemberPrice) {
      const memberPrice = memberAdjustedPrice(basePrice);
      const strikePrice = hasDiscount ? compareAt : basePrice;
      return `
        <span class="igComparePrice"${attrs}>${formatUSD(strikePrice)}</span>
        <span class="igPrice"${attrs}>${formatUSD(memberPrice)}</span>
      `;
    }

    return hasDiscount
      ? `<span class="igComparePrice"${attrs}>${formatUSD(compareAt)}</span><span class="igPrice"${attrs}>${formatUSD(basePrice)}</span>`
      : `<span class="igPrice"${attrs}>${formatUSD(basePrice)}</span>`;
  }

  function renderMemberMessage(pricing, memberEligible, handle, variantId) {
    if (!memberEligible) return '';

    const isMember = window.EPIC_GARDENING?.isGreenhouseMember === true;
    if (isMember) {
      return '';
    }

    const basePrice = Number(pricing?.price) || 0;
    const memberPrice = memberAdjustedPrice(basePrice);
    return `
      <p class="member-savings member-savings-clp member-savings-search" data-card-member-state="guest">
        <a href="/products/${handle}?variant=${variantId}&member-price=membership" class="member-savings__link">Member Price <span class="member-savings-amount">${formatUSD(memberPrice)}</span></a>
      </p>
    `;
  }

  function renderPackQtyDropdown(hit, pricing) {
    const handle = String(hit?.handle || '');
    if (!PACK_QTY_HANDLES.has(handle)) return '';

    const basePrice = Number(pricing?.price);
    const hasBasePrice = Number.isFinite(basePrice) && basePrice >= 0;
    const basePriceCents = Math.round(basePrice * 100);
    const idKey = String(hit?.id || hit?.objectID || handle);
    const selectId = `eg-search-qty-${idKey}`;

    const is1020 = handle === '1020-universal-bottom-tray';
    const is16Cell = handle === 'epic-16-cell-seed-starting-trays' || handle === 'epic-16-cell-seed-starting-trays-pack';
    const is5Cube = handle === '5-cube-pot';
    const is3Cube = handle === '3-cube-pot';
    const dropdownTextMode = 'default';

    let labelText = 'Packs';
    if (is1020) labelText = 'Trays';
    else if (is5Cube) labelText = 'Pots';
    else if (is3Cube) labelText = 'Pots';

    const max = is1020 ? 30 : (is3Cube ? 20 : 24); // 1020 goes to 30, 3-cube to 20, others to 24

    const options = Array.from({ length: max }, (_, idx) => idx + 1).map((i) => {
      let suffix = '';

      if (hasBasePrice) {
        const discount = packQtyDiscountForCount(i);
        // Cart applies the tier per unit and rounds fractional cents up.
        const unitPriceCents = Math.ceil(basePriceCents * (1 - discount));
        const divisor = is16Cell ? 4 : 1;
        const unitLabel = (is5Cube || is3Cube) ? 'Pot' : (is1020 || is16Cell ? 'Tray' : 'Pack');
        suffix = ` - (${formatUSD(unitPriceCents / (100 * divisor))}/${unitLabel})`;
      }

      const noun = is1020 ? 'Tray' : (is5Cube || is3Cube ? 'Pot' : 'Pack');
      const plural = i === 1 ? '' : 's';

      // Dynamic Text Logic
      let basicText = `${i} ${noun}${plural}`;
      const verboseText = `${basicText}${suffix}`;

      let attrs = '';
      let displayText = verboseText;

      if (dropdownTextMode === 'default' && i === 1) {
        displayText = basicText;
        attrs = `data-simple-label="${basicText}" data-verbose-label="${verboseText}"`;
      } else if (dropdownTextMode === 'verbose_only') {
        // Keep full CLP-style text in both collapsed and expanded states.
        attrs = `data-simple-label="${verboseText}" data-verbose-label="${verboseText}"`;
      }

      return `<option value="${i}" ${i === 1 ? 'selected' : ''} ${attrs}>${displayText}</option>`;
    }).join('');

    return `
      <div class="card-variant-option eg-search-event-prevention">
        <label class="card-variant-label" for="${selectId}">${labelText}:</label>
        <div class="card-variant-select-wrapper">
          <select id="${selectId}"
                  class="eg-search-qty-select"
                  data-eg-qty-select="1"
                  data-eg-force-verbose="${dropdownTextMode === 'verbose_only' ? 'true' : 'false'}"
                  onclick="event.stopPropagation();"
                  onfocus="this.dataset.expanded='true'; if(window.EgUpdateDropdown) EgUpdateDropdown(this);"
                  onblur="this.dataset.expanded='false'; if(window.EgUpdateDropdown) EgUpdateDropdown(this);"
                  onchange="this.dataset.expanded='false'; if(window.EgUpdateDropdown) EgUpdateDropdown(this); this.blur();"
                  onmousedown="this.dataset.expanded='true'; if(window.EgUpdateDropdown) EgUpdateDropdown(this);">
            ${options}
          </select>
        </div>
      </div>
    `;
  }

  function refreshPackQtyDropdown(card, priceCents) {
    const select = card.querySelector('.eg-search-qty-select');
    if (!select || !Number.isFinite(Number(priceCents))) return;
    const holder = document.createElement('div');
    holder.innerHTML = renderPackQtyDropdown({ handle: card.dataset.handle, id: card.dataset.productId }, { price: Number(priceCents) / 100 });
    const next = holder.querySelector('select');
    if (!next) return;
    const selectedQuantity = select.value;
    select.innerHTML = next.innerHTML;
    select.value = selectedQuantity;
  }

  function renderProductCardFull(hit, index = 0, query = '', searchId = '') {
    const pricing = resolveDisplayPrices(hit);
    const hasDiscount = pricing.hasDiscount;
    const hasMultipleVariants = (hit.variants_count || 1) > 1 && String(hit?.handle || '') !== '3-cube-pot';
    const variantId = hit.variant_id || hit.objectID;
    const handle = String(hit?.handle || '');
    const cardMetadata = hit?.epic_card_metadata || {};
    const variantPreorderState = cardMetadata.variantPreorderStates?.[Number(variantId)] || {
      active: cardMetadata.customPreorderActive === true,
      timeline: String(cardMetadata.preorderTimeline || '').trim(),
    };
    const customPreorderActive = variantPreorderState.active === true;
    const preorderTimeline = String(variantPreorderState.timeline || '').trim();
    const virtualBundleVariants = Array.isArray(cardMetadata.virtualBundleVariants)
      ? cardMetadata.virtualBundleVariants
      : [];
    const isVirtualBundle = virtualBundleVariants.length > 0;
    const virtualBundleQuantity = Math.max(1, Number(cardMetadata.virtualBundleQuantity) || 1);
    const requiresMappedInventory = isVirtualBundle || isMappedComponentInventoryHandle(handle);
    const preorderStatesForCard = isVirtualBundle
      ? (cardMetadata.virtualBundlePreorderStates || {})
      : (cardMetadata.variantPreorderStates || {});
    const awaitingMappedInventoryAttrs = requiresMappedInventory
      ? 'data-awaiting-mapped-inventory="true" disabled aria-disabled="true"'
      : '';
    const discountPercent = pricing.discountPercent;
    const memberEligible = isMemberPricingEligible(hit);

    // Badges based on tags
    const tags = hit.tags || [];
    const tagsLower = tags.map(t => (t || '').toLowerCase());
    const isNew = tagsLower.includes('new') || tagsLower.includes('new arrival') || tagsLower.includes('new-arrival');
    const isOrganic = hitHasOrganicBadge(hit);
    const isMember = window.EPIC_GARDENING?.isGreenhouseMember === true;
    const showMemberPrice = isMember && memberEligible;
    const earlyAccessEnabled = hit.early_access_enabled === true || hit.early_access_enabled === 'true';
    const earlyAccessStart = hit.early_access_start_date ? new Date(hit.early_access_start_date) : null;
    const earlyAccessEnd = hit.early_access_end_date ? new Date(hit.early_access_end_date) : null;
    const earlyAccessNow = new Date();
    const isInEarlyAccess = earlyAccessEnabled
      && earlyAccessStart
      && !Number.isNaN(earlyAccessStart.getTime())
      && earlyAccessEnd
      && !Number.isNaN(earlyAccessEnd.getTime())
      && earlyAccessNow >= earlyAccessStart
      && earlyAccessNow < earlyAccessEnd;
    const blockAccess = isInEarlyAccess && !isMember;

    // Top-right promo badge HTML
    const promoBadges = [];
    if (isOrganic) {
      promoBadges.push(`<span class="badge badge--top-right organic">Organic</span>`);
    }
    if (blockAccess) {
      promoBadges.push(`<span class="badge badge--top-right badge--early-access">New Product Launch</span>`);
    } else if (isNew) {
      promoBadges.push(`<span class="badge badge--top-right">New</span>`);
    }

    // Discount badge should render first (left-most) in the top badge row
    const discountBadgeHtml = hasDiscount
      ? `<span class="badge badge--top-right ais-hit--discount-badge">${discountPercent}% off</span>`
      : '';

    // Keep discount first, then promo badge(s) to the right
    const promoBadgesHtml = promoBadges.join('');
    const topBadgesHtml = (discountBadgeHtml || promoBadgesHtml)
      ? `<div class="ais-hit--badge-row">${discountBadgeHtml}${promoBadgesHtml}</div>`
      : '';

    return `
      <div class="ais-Hits-item">
        <div class="ais-hit ais-product" 
             data-product-id="${hit.id}"
             data-variant-id="${hit.objectID}"
             data-selected-variant-id="${variantId}"
             data-handle="${hit.handle}"
             data-member-eligible="${memberEligible ? 'true' : 'false'}"
             data-custom-preorder-card="${customPreorderActive ? 'true' : 'false'}"
             data-preorder-variants="${config.escapeHtml(JSON.stringify(preorderStatesForCard))}"
             data-is-virtual-bundle="${isVirtualBundle ? 'true' : 'false'}"
             data-bundle-quantity="${virtualBundleQuantity}"
             data-virtual-bundle-variants="${config.escapeHtml(JSON.stringify(virtualBundleVariants))}"
             data-eg-query="${config.escapeHtml(query)}"
             data-eg-search-id="${config.escapeHtml(searchId)}"
             data-eg-position="${index + 1}"
             data-early-access-enabled="${hit.early_access_enabled || ''}"
             data-early-access-start="${config.escapeHtml(hit.early_access_start_date || '')}"
             data-early-access-end="${config.escapeHtml(hit.early_access_end_date || '')}">
             
          <div class="ais-hit--picture-container">
            ${topBadgesHtml}
            <a href="/products/${handle}"
               data-eg-track-click="1"
               data-eg-query="${config.escapeHtml(query)}"
               data-eg-search-id="${config.escapeHtml(searchId)}"
               data-eg-product-id="${config.escapeHtml(String(hit.id || hit.objectID || ''))}"
               data-eg-product-title="${config.escapeHtml(hit.title || '')}"
               data-eg-position="${index + 1}"
               data-eg-index-name="products"
               data-eg-search-type="search">
              <img class="ais-hit--picture" 
                   src="${config.escapeHtml(resultImageUrl(hit.image_url))}"
                   srcset="${config.escapeHtml(resultImageSrcset(hit.image_url))}"
                   sizes="${RESULT_IMAGE_SIZES}"
                   alt="${config.escapeHtml(hit.title || '')}"
                   loading="${index < 2 ? 'eager' : 'lazy'}"
                   fetchpriority="${index === 0 ? 'high' : 'auto'}" />
            </a>
          </div>
          
          <div class="ais-hit--details">
            <div data-eg-star-rating data-oke-reviews-product-id="shopify-${hit.id}"></div>
            <p class="ais-hit--title">
              <a href="/products/${handle}"
                 data-eg-track-click="1"
                 data-eg-query="${config.escapeHtml(query)}"
                 data-eg-search-id="${config.escapeHtml(searchId)}"
                 data-eg-product-id="${config.escapeHtml(String(hit.id || hit.objectID || ''))}"
                 data-eg-product-title="${config.escapeHtml(hit.title || '')}"
                 data-eg-position="${index + 1}"
                 data-eg-index-name="products"
                 data-eg-search-type="search">
                ${hit._formatted?.title || hit.title}
              </a>
            </p>
            <p class="ais-hit--price ${(hasDiscount || showMemberPrice) ? 'has-discount' : ''}">
              ${renderSearchPriceLine(pricing, showMemberPrice, hit.id, variantId)}
            </p>
            <div class="ais-hit--member-message">
              ${renderMemberMessage(pricing, memberEligible, handle, variantId)}
            </div>
            ${renderPackQtyDropdown(hit, pricing)}
            ${hasMultipleVariants ? `
              <div class="card-variant-selector">
                <div id="variant-options-${variantId}" class="variant-options">
                  <!-- Variant selectors loaded dynamically -->
                </div>
              </div>
            ` : ''}
            <div class="ais-hit--actions">
              ${(() => {
                if (blockAccess) {
                  return `
                    <a href="/products/${handle}" 
                      class="quick-add__submit button button--full-width ais-hit--button button--early-access">
                      <span>Greenhouse Early Access</span>
                    </a>
                  `;
                }

                return `
                  <button class="quick-add__submit button button--full-width ais-hit--button ais-hit--add-to-cart"
                          data-sold-out-message="true"
                          data-variant-id="${variantId}"
                          data-eg-query="${config.escapeHtml(query)}"
                          data-eg-search-id="${config.escapeHtml(searchId)}"
                          data-eg-position="${index + 1}"
                          ${awaitingMappedInventoryAttrs}
                          onclick="customSearchAddToCartWithVariant(this, event)">
                    <span>${customPreorderActive ? 'Pre-Order' : 'Add to cart'}</span>
                    <span class="sold-out-message hidden">Sold out</span>
                    <div class="loading__spinner hidden">
                      <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                      </svg>
                    </div>
                  </button>
                `;
              })()}
            </div>
            ${customPreorderActive && !blockAccess && preorderTimeline
              ? `<p class="ais-hit--preorder-timeline">${config.escapeHtml(preorderTimeline)}</p>`
              : ''}
          </div>
        </div>
      </div>
    `;
  }

  function triggerOkendoReviews(rootEl) {
    const root = rootEl || document;
    const els = root.querySelectorAll?.('[data-eg-star-rating]:not([data-oke-star-rating])') || [];
    els.forEach((el) => el.setAttribute('data-oke-star-rating', ''));

    try {
      if (typeof window.okeWidgetApi !== 'undefined') {
        els.forEach((el) => {
          if (typeof window.okeWidgetApi.initWidget === 'function') {
            window.okeWidgetApi.initWidget(el);
          }
        });
        return;
      }

      if (typeof window.Okendo !== 'undefined' && typeof window.Okendo.initWidget === 'function') {
        window.Okendo.initWidget();
        els.forEach((el) => el.dispatchEvent(new CustomEvent('oke:init', { bubbles: true })));
      }
    } catch (e) {
      console.warn('[EG Search] Okendo init error:', e);
    }
  }

  function renderHits(hits, container, currentQuery, searchId) {
    if (!container) return;
    if (!hits || !hits.length) {
      container.innerHTML = '<p class="no-results">No products found. Try adjusting your filters.</p>';
      return;
    }

    container.innerHTML = `
      <div class="ais-Hits-list">
        ${hits.map((hit, idx) => renderProductCardFull(hit, idx, currentQuery, searchId)).join('')}
      </div>
    `;

    // Prioritize the first row; hydrate later cards as they approach the viewport.
    window.EGSearchVariants?.scheduleCards(hits, container);

    refreshIntelligemsPrices();
  }

  function formatFacetName(name) {
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  function renderFacets(facets, container, currentFilters, openByDefault, currentQuery) {
    if (!container || !facets) return;

    const price = (currentFilters && currentFilters._priceRange) ? currentFilters._priceRange : {};
    const priceMin = (price && price.gte !== undefined) ? price.gte : '';
    const priceMax = (price && price.lte !== undefined) ? price.lte : '';

    const availabilitySelected = Array.isArray(currentFilters?.inventory_available) ? currentFilters.inventory_available : [];
    const isAvailabilityOpen = availabilitySelected.length > 0;

    const availabilitySection = `
      <details class="ais-facet-dropdown-wrapper" ${isAvailabilityOpen ? 'open' : ''}>
        <summary class="ais-facet--header">
          Availability
          ${CARET_SVG}
        </summary>
        <div class="ais-RefinementList">
          <label class="ais-facet--label">
            <input type="checkbox"
                   class="ais-dropdown-checkbox"
                   ${(availabilitySelected || []).includes(true) ? 'checked' : ''}
                   onchange="customSearchToggleFilter('inventory_available', true)" />
            <span>In stock</span>
          </label>
          <label class="ais-facet--label">
            <input type="checkbox"
                   class="ais-dropdown-checkbox"
                   ${(availabilitySelected || []).includes(false) ? 'checked' : ''}
                   onchange="customSearchToggleFilter('inventory_available', false)" />
            <span>Out of stock</span>
          </label>
        </div>
      </details>
    `;

    const isPriceOpen = Boolean(priceMin || priceMax);
    const priceSection = `
      <details class="ais-facet-dropdown-wrapper" ${isPriceOpen ? 'open' : ''}>
        <summary class="ais-facet--header">
          Price
          ${CARET_SVG}
        </summary>
        <div class="ais-RefinementList">
          <div class="meili-price-range">
            <input type="number" inputmode="decimal" class="meili-price-range__input" placeholder="Min" value="${config.escapeHtml(String(priceMin))}" />
            <span class="meili-price-range__sep">-</span>
            <input type="number" inputmode="decimal" class="meili-price-range__input" placeholder="Max" value="${config.escapeHtml(String(priceMax))}" />
            <button type="button" class="meili-price-range__apply button button--secondary">Apply</button>
          </div>
        </div>
      </details>
    `;

    const facetSections = Object.entries(facets)
      .filter(([facetName, values]) => {
        // Skip facets with no values.
        return values && typeof values === 'object' && Object.keys(values).length > 0;
      })
      .map(([facetName, values]) => {
        const hasActiveFilters = currentFilters[facetName] && currentFilters[facetName].length > 0;
        const isOpen = Boolean(hasActiveFilters);

        return `
        <details class="ais-facet-dropdown-wrapper" ${isOpen ? 'open' : ''}>
          <summary class="ais-facet--header">
            ${formatFacetName(facetName)}
            ${CARET_SVG}
          </summary>
          <div class="ais-RefinementList">
            ${Object.entries(values).slice(0, 20).map(([value, count]) => {
          const safeVal = String(value);
          const isChecked = (currentFilters[facetName] || []).includes(safeVal);
          const safeValForOnChange = safeVal.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
          return `
                <label class="ais-facet--label">
                  <input type="checkbox"
                         class="ais-dropdown-checkbox"
                         data-facet="${facetName}"
                         data-value="${config.escapeHtml(safeVal)}"
                         ${isChecked ? 'checked' : ''}
                         onchange="customSearchToggleFilter('${facetName}', '${config.escapeHtml(safeValForOnChange)}')" />
                  <span>${config.escapeHtml(safeVal)}</span>
                  <span class="ais-facet--count">(${count})</span>
                </label>
              `;
        }).join('')}
          </div>
        </details>
      `;
      }).join('');

    container.innerHTML = availabilitySection + priceSection + facetSections;

    // Hook up the price "Apply" button.
    try {
      const priceWrap = container.querySelector('.meili-price-range');
      if (priceWrap) {
        const inputs = priceWrap.querySelectorAll('.meili-price-range__input');
        const btn = priceWrap.querySelector('.meili-price-range__apply');
        const apply = () => {
          const min = inputs && inputs[0] ? inputs[0].value : '';
          const max = inputs && inputs[1] ? inputs[1].value : '';
          window.customSearchSetPriceRange?.(min, max);
        };
        if (btn) btn.addEventListener('click', apply);
        inputs.forEach((inp) => inp.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') apply();
        }));
      }
    } catch (e) {
      // no-op
    }
  }

  function renderPagination(data, container) {
    if (!container) return;
    if (!data || data.nbPages <= 1) {
      container.innerHTML = '';
      return;
    }

    let html = '<div class="ais-Pagination">';

    const maxPages = 5;
    let startPage = Math.max(0, data.page - Math.floor(maxPages / 2));
    let endPage = Math.min(data.nbPages - 1, startPage + maxPages - 1);
    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(0, endPage - maxPages + 1);
    }

    if (data.page > 0) {
      html += `<button class="ais-Pagination-item" onclick="customSearchGoToPage(0)" title="First Page">&laquo;</button>`;
      html += `<button class="ais-Pagination-item" onclick="customSearchGoToPage(${data.page - 1})" title="Previous Page">&lt;</button>`;
    }

    for (let i = startPage; i <= endPage; i++) {
      html += `<button class="ais-Pagination-item ${i === data.page ? 'ais-Pagination-item--selected' : ''}" 
                       onclick="customSearchGoToPage(${i})">${i + 1}</button>`;
    }

    if (data.page < data.nbPages - 1) {
      html += `<button class="ais-Pagination-item" onclick="customSearchGoToPage(${data.page + 1})">&gt;</button>`;
      html += `<button class="ais-Pagination-item" onclick="customSearchGoToPage(${data.nbPages - 1})">&raquo;</button>`;
    }

    html += '</div>';
    container.innerHTML = html;
  }

  function renderActiveFilters(currentFilters, container) {
    if (!container) return;

    let html = '';
    let hasFilters = false;

    // Add one filter "chip" button.
    const addChip = (label, facet, value) => {
      hasFilters = true;
      // Value needs escaping for the function call
      const isBool = typeof value === 'boolean';
      const safeVal = String(value).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
      const onClickVal = isBool ? String(value) : `'${config.escapeHtml(safeVal)}'`;
      html += `
        <button class="active-filter-chip" onclick="customSearchToggleFilter('${facet}', ${onClickVal})" style="display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; background: #f4f4f4; border-radius: 20px; font-size: 13px; cursor: pointer; border: 1px solid #ddd; margin-right: 8px; margin-bottom: 8px;">
          ${label} <span class="active-filter-close" style="font-weight: bold; margin-left: 4px;">✕</span>
        </button>
      `;
    };

    // 1. Price Range
    if (currentFilters._priceRange) {
      const { gte, lte } = currentFilters._priceRange;
      if (gte !== undefined || lte !== undefined) {
        const label = `Price: $${gte || 0} - $${lte || '∞'}`;
        hasFilters = true;
        html += `
               <button class="active-filter-chip" onclick="customSearchSetPriceRange('', '')" style="display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; background: #f4f4f4; border-radius: 20px; font-size: 13px; cursor: pointer; border: 1px solid #ddd; margin-right: 8px; margin-bottom: 8px;">
                 ${config.escapeHtml(label)} <span class="active-filter-close" style="font-weight: bold; margin-left: 4px;">✕</span>
               </button>
             `;
      }
    }

    // 2. Inventory
    if (currentFilters.inventory_available) {
      currentFilters.inventory_available.forEach(val => {
        const label = val ? 'In Stock' : 'Out of Stock';
        addChip(label, 'inventory_available', val);
      });
    }

    // 3. Other Facets
    Object.keys(currentFilters).forEach(key => {
      if (key === '_priceRange' || key === 'inventory_available') return;
      const values = currentFilters[key];
      if (Array.isArray(values)) {
        values.forEach(val => {
          const label = `${formatFacetName(key)}: ${val}`;
          addChip(config.escapeHtml(label), key, val);
        });
      }
    });

    if (hasFilters) {
      html += `
          <button class="active-filter-clear-all" onclick="customSearchClearAll()" style="text-decoration: underline; font-size: 13px; margin-left: 8px; background: none; border: none; cursor: pointer;">
            Clear All
          </button>
        `;
    }

    container.innerHTML = html;
    container.style.display = hasFilters ? 'block' : 'none';
    if (hasFilters) {
      container.style.marginBottom = '16px';
    }
  }

  return {
    resultImageUrl,
    dedupeHitsByHandle,
    preloadFirstImage,
    resultImageSrcset,
    renderSortDropdown,
    renderStats,
    renderProductCardFull,
    refreshPackQtyDropdown,
    triggerOkendoReviews,
    renderHits,
    renderFacets,
    renderPagination,
    formatFacetName,
    renderActiveFilters,
    refreshIntelligemsPrices,
  };
})();
